/**
 * Reportes → Consumo interno ejecutivo (`/reportes/consumo-interno`).
 *
 * Puerto de `Reportes::consumo_interno_ejecutivo` + `Reportes_model::obtener_datos_consumo_interno`
 * + `views/menu/reportes/consumo_interno_ejecutivo.php`: las cargas de combustible
 * **propio** (las que el APP registra como consumo interno), con su producto, su
 * lado, su volumen y su importe.
 *
 * La pantalla del panel anterior pedía **fecha inicio, fecha fin, producto y
 * placa** y listaba cliente, documento, placa, producto, lado, volumen, importe,
 * kilómetros, observaciones y el usuario que lo registró.
 *
 * Reglas conservadas:
 * - El producto, el lado, el volumen y el importe viven en
 *   `internal_consumption.additional_data` (`{ fuel_grade, pump, volume, amount }`),
 *   tal como los guarda el APP; las filas sin combustible se listan con sus valores
 *   en cero y **no** entran en los totales (en el original salían con '-' y 0).
 * - Los filtros son el rango de fechas (`DATE(created_at)`), el producto
 *   (combustible) y la placa.
 *
 * Diferencias anotadas:
 * - El filtro por producto se aplica en la **consulta** (`additional_data ->
 *   '$.fuel_grade'`): la pantalla anterior traía todo el rango y descartaba filas
 *   en PHP, así que la tabla y el pie podían no cuadrar con el filtro.
 * - Se añaden el reparto por producto y por día, las tarjetas del periodo y el CSV
 *   y el PDF (el original era sólo la tabla en pantalla).
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosConsumoInterno,
  useConsumoInterno,
  type FiltrosConsumoInterno,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fecha local `AAAA-MM-DD` (sin desfasar por la zona horaria). */
function fechaLocal(fecha: Date): string {
  return `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate(),
  ).padStart(2, '0')}`;
}

/** Rango por defecto: el mes en curso (como el panel anterior). */
function rangoPorDefecto(): { desde: string; hasta: string } {
  const hoy = new Date();

  return {
    desde: `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`,
    hasta: fechaLocal(hoy),
  };
}

/** «agosto 2026» a partir de `2026-08`. */
function etiquetaMes(periodo: string | null | undefined): string {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return periodo ?? '';

  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

/** Primer y último día de un mes `AAAA-MM`. */
function mesComoRango(periodo: string): { desde: string; hasta: string } {
  const anio = Number(periodo.slice(0, 4));
  const mes = Number(periodo.slice(5, 7));

  return {
    desde: `${periodo}-01`,
    hasta: fechaLocal(new Date(anio, mes, 0)),
  };
}

export function ReporteConsumoInternoPage() {
  useDocumentTitle('Reportes · Consumo interno ejecutivo');

  const [filtros, setFiltros] = useState<FiltrosConsumoInterno>(() => ({
    ...rangoPorDefecto(),
    producto: '',
    placa: '',
  }));
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosConsumoInterno();
  const consulta = useConsumoInterno(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const meses = catalogos.data?.meses ?? [];
  const mesesConDatos = useMemo(() => meses.map((mes) => mes.periodo), [meses]);
  const registros = datos?.registros ?? [];
  // El listado se pagina en el navegador; los repartos y el pie siguen usando el listado
  // completo.
  const paginacion = usePaginacion(registros, { clave: filtros });

  const cambiar = <C extends keyof FiltrosConsumoInterno>(
    campo: C,
    valor: FiltrosConsumoInterno[C],
  ) => setFiltros((previos) => ({ ...previos, [campo]: valor }));

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarConsumoInterno(filtros), {
        nombre: `consumo-interno-${filtros.desde}_${filtros.hasta}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfConsumoInterno(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Consumo interno ejecutivo</h1>
          <p className="mt-1 text-sm text-ink-500">
            Cargas de combustible propio del periodo, con su producto, su lado y su importe.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
          <Button
            variant="outline"
            onClick={() => void consulta.refetch()}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Actualizar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.desde}
                  onChange={(evento) => cambiar('desde', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.hasta}
                  onChange={(evento) => cambiar('hasta', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Producto</span>
              <Select
                value={filtros.producto}
                onChange={({ target }) => cambiar('producto', target.value)}
                className={CONTROL}
                aria-label="Producto"
              >
                <option value="">Todos los combustibles</option>
                {(catalogos.data?.productos ?? []).map((producto) => (
                  <option key={producto} value={producto}>
                    {producto}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Placa</span>
              <input
                value={filtros.placa}
                onChange={(evento) => cambiar('placa', evento.target.value.toUpperCase())}
                placeholder="Placa del vehículo…"
                className={CONTROL}
                aria-label="Placa"
              />
            </label>
          </div>

          {mesesConDatos.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                <Search className="size-3.5" aria-hidden="true" />
                Meses con consumos
              </span>
              {meses.slice(0, 12).map((mes) => {
                const rango = mesComoRango(mes.periodo);
                const activo = rango.desde === filtros.desde && rango.hasta === filtros.hasta;

                return (
                  <button
                    key={mes.periodo}
                    type="button"
                    aria-pressed={activo}
                    onClick={() => setFiltros((previos) => ({ ...previos, ...rango }))}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                      activo
                        ? 'border-brand-300 bg-brand-50 text-brand-700'
                        : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                    )}
                    title={`${formatNumber(mes.registros)} registro(s)`}
                  >
                    {etiquetaMes(mes.periodo)}
                  </button>
                );
              })}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      {resumen && resumen.registros === 0 ? (
        <Alert variant="info" title="Sin consumos en el rango elegido">
          <p>
            Esta copia tiene consumos cargados entre {catalogos.data?.rango.desde} y{' '}
            {catalogos.data?.rango.hasta}.
          </p>
          {meses[0] ? (
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => {
                const primero = meses[0];
                if (!primero) return;

                setFiltros((previos) => ({ ...previos, ...mesComoRango(primero.periodo) }));
              }}
            >
              Ver {etiquetaMes(meses[0]?.periodo)}
            </Button>
          ) : null}
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            etiqueta: 'Registros',
            valor: formatNumber(resumen?.registros ?? 0),
            detalle: `${formatNumber(resumen?.cargas ?? 0)} carga(s) con combustible`,
          },
          {
            etiqueta: 'Volumen',
            valor: formatNumber(resumen?.volumen ?? 0),
            detalle: 'según la unidad del producto',
          },
          {
            etiqueta: 'Importe',
            valor: formatCurrency(resumen?.importe ?? 0),
            detalle: 'valorizado de las cargas',
          },
          {
            etiqueta: 'Clientes',
            valor: formatNumber(resumen?.clientes ?? 0),
            detalle: `${formatNumber(resumen?.vehiculos ?? 0)} vehículo(s)`,
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por producto</h2>

            <div className="max-h-72 overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Producto</th>
                    <th className="w-[5rem] px-2 py-2 text-right">Cargas</th>
                    <th className="w-[6rem] px-2 py-2 text-right">Volumen</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {(datos?.por_producto ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-2 py-6 text-center text-sm text-ink-500">
                        Sin cargas en el rango.
                      </td>
                    </tr>
                  ) : null}

                  {(datos?.por_producto ?? []).map((fila) => (
                    <tr key={fila.producto} className="border-t border-ink-100">
                      <td className="px-2 py-2 text-ink-800">{fila.producto}</td>
                      <td className="px-2 py-2 text-right text-ink-600">
                        {formatNumber(fila.cargas)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.volumen)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.importe)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-3 p-4">
            <h2 className="text-sm font-semibold text-ink-800">Por día</h2>

            <div className="max-h-72 overflow-auto rounded-card border border-ink-100">
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-2">Fecha</th>
                    <th className="w-[5rem] px-2 py-2 text-right">Cargas</th>
                    <th className="w-[6rem] px-2 py-2 text-right">Volumen</th>
                    <th className="w-[6.5rem] px-2 py-2 text-right">Importe</th>
                  </tr>
                </thead>
                <tbody>
                  {(datos?.por_dia ?? []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-2 py-6 text-center text-sm text-ink-500">
                        Sin cargas en el rango.
                      </td>
                    </tr>
                  ) : null}

                  {(datos?.por_dia ?? []).map((fila) => (
                    <tr key={fila.fecha} className="border-t border-ink-100">
                      <td className="px-2 py-2 text-ink-800">{formatDateValue(fila.fecha)}</td>
                      <td className="px-2 py-2 text-right text-ink-600">
                        {formatNumber(fila.cargas)}
                      </td>
                      <td className="px-2 py-2 text-right text-ink-700">
                        {formatNumber(fila.volumen)}
                      </td>
                      <td className="px-2 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.importe)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Consumos del periodo ({formatNumber(resumen?.registros ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {formatDateValue(filtros.desde)} — {formatDateValue(filtros.hasta)}
              {filtros.producto ? ` · ${filtros.producto}` : ''}
              {filtros.placa ? ` · placa ${filtros.placa}` : ''}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full max-lg:min-w-[48rem] table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[5.5rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[7.5rem] px-1.5 py-2">Cliente</th>
                    <th className="w-[5rem] px-1.5 py-2">Placa</th>
                    <th className="w-[6.5rem] px-1.5 py-2">Producto</th>
                    <th className="w-[4rem] px-1.5 py-2">Lado</th>
                    <th className="w-[4.5rem] px-1.5 py-2 text-right">Volumen</th>
                    <th className="w-[5.5rem] px-1.5 py-2 text-right">Importe</th>
                    <th className="w-[5.5rem] px-1.5 py-2">Usuario</th>
                  </tr>
                </thead>
                <tbody>
                  {registros.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={8} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay consumos que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila) => (
                    <tr
                      key={fila.id}
                      className={cn(
                        'border-t border-ink-100 hover:bg-brand-50/30',
                        !fila.es_carga && 'text-ink-400',
                      )}
                    >
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold">{fila.fecha}</p>
                        {fila.kilometraje ? (
                          <p className="truncate text-[11px] text-ink-500">
                            {formatNumber(Number(fila.kilometraje))} km
                          </p>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate" title={fila.cliente ?? ''}>
                          {fila.cliente ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">
                          {fila.tipo_documento ?? ''} {fila.documento ?? ''}
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate">{fila.placa || '—'}</p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800">{fila.producto}</p>
                        {!fila.es_carga ? (
                          <Badge variant="neutral" className="mt-0.5 px-1.5 py-0 text-[10px]">
                            sin combustible
                          </Badge>
                        ) : null}
                      </td>
                      <td className="px-1.5 py-2 text-ink-600">
                        <p className="truncate">{fila.lado}</p>
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        {formatNumber(fila.volumen)}
                      </td>
                      <td className="px-1.5 py-2 text-right font-semibold text-ink-900">
                        {formatCurrency(fila.importe)}
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-600" title={fila.usuario ?? ''}>
                          {fila.usuario ?? '—'}
                        </p>
                        {fila.observaciones ? (
                          <p
                            className="truncate text-[11px] text-ink-500"
                            title={fila.observaciones}
                          >
                            {fila.observaciones}
                          </p>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
                {registros.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={5}>
                        SUMA · {formatNumber(resumen?.cargas ?? 0)} carga(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatNumber(resumen?.volumen ?? 0)}
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatCurrency(resumen?.importe ?? 0)}
                      </td>
                      <td className="px-1.5 py-2" />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="consumos" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Cada fila es un consumo que el APP registró en el turno (con el producto, el lado, el
        volumen y el importe en sus datos adicionales). Las filas sin combustible —las que el APP
        usa para otras operaciones— se listan en gris y no entran en los totales. El filtro por
        producto se aplica en la consulta, así que el pie siempre cuadra con lo que se ve.
      </p>
    </div>
  );
}
